import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useFormHook, useSetError } from '@Hooks/index';
import { useSetCardShipmentAddress } from '@Hooks/api/useSetCardShipmentAddress';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/useBoundStore';
import { OnboardingFormFields } from '@Types/index';
import { useGetCountries } from '@Hooks/api/useGetCountries';
import { RoutesMap } from '@Router/router.types';
import { useValidateAutoComplete } from '@Hooks/providers/useValidateAutoComplete';
import { MailingAddressSchema } from '../mailingAddress/MailingAddressSchema';
import { syncCardShipmentToStore } from '../MailingAddress.service';
import type { CardShipmentAddressType } from '@Store/slices/customer/customer.types';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { IMailingAddressForm } from '../mailingAddress/MailingAddressForm';

const CARD_SHIPMENT_FIELD = 'cardShipmentAddressType';

const cardShipmentSchema = z
  .object({
    [CARD_SHIPMENT_FIELD]: z.enum(['REGISTERED', 'OPERATIONAL', 'MAILING']),
    [OnboardingFormFields.LINE1]: z.any().optional(),
    [OnboardingFormFields.APARTMENT]: z.string().optional(),
    [OnboardingFormFields.CITY]: z.any().optional(),
    [OnboardingFormFields.STATE]: z.any().optional(),
    [OnboardingFormFields.ZIP]: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data[CARD_SHIPMENT_FIELD] !== 'MAILING') return;
    const result = MailingAddressSchema.safeParse(data);
    if (!result.success) {
      result.error.issues.forEach((issue) =>
        ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path })
      );
    }
  });

interface CardShipmentFormValues extends IMailingAddressForm {
  [CARD_SHIPMENT_FIELD]: CardShipmentAddressType;
}

export const useCardShipmentLogic = () => {
  const { t } = useTranslation();
  const { navigateToNextPage } = useNavigationManager();
  const {
    customer: {
      customerData: { businessAddressData, mailingAddressData, cardShipmentAddressType },
    },
  } = useBoundStore((state) => state);

  const { data: countriesData } = useGetCountries(true, false);
  const usCountry = countriesData?.countries.find((c) => c.code === 'US');
  const usStates = usCountry?.subDivisions?.map((s) => ({ title: s.name, value: s.code }));

  const isOperationalUS = !businessAddressData.country || businessAddressData.country === 'US';

  const options: { value: CardShipmentAddressType; label: string }[] = isOperationalUS
    ? [
        { value: 'REGISTERED', label: t('pages.cardShipment.options.registered') },
        { value: 'OPERATIONAL', label: t('pages.cardShipment.options.operational') },
        { value: 'MAILING', label: t('pages.cardShipment.options.mailing') },
      ]
    : [
        { value: 'REGISTERED', label: t('pages.cardShipment.options.registered') },
        { value: 'MAILING', label: t('pages.cardShipment.options.mailing') },
      ];

  const defaultValues: CardShipmentFormValues = {
    [CARD_SHIPMENT_FIELD]: cardShipmentAddressType ?? 'REGISTERED',
    [OnboardingFormFields.LINE1]: mailingAddressData?.line1 || '',
    [OnboardingFormFields.APARTMENT]: mailingAddressData?.line2 || '',
    [OnboardingFormFields.CITY]: mailingAddressData?.city || '',
    [OnboardingFormFields.STATE]: null,
    [OnboardingFormFields.ZIP]: mailingAddressData?.postalCode || '',
  };

  const formMethods: ExtendedUseFormReturn<CardShipmentFormValues> = useFormHook({
    formSchema: cardShipmentSchema,
    formDefaultValues: defaultValues,
    schemaTitle: 'cardShipmentAddress',
    mode: 'onTouched',
  });

  const selectedType = formMethods.watch(CARD_SHIPMENT_FIELD);
  const showMailingForm = selectedType === 'MAILING';

  const { mutateAsync: asyncSetCardShipment, isPending } = useSetCardShipmentAddress((_, input) => {
    syncCardShipmentToStore(input.cardShipmentAddressType, formMethods.getValues());
    navigateToNextPage(RoutesMap.MAILING_ADDRESS);
  });

  const handleSubmit = useCallback(async () => {
    const formData = formMethods.getValues();
    const dto: {
      cardShipmentAddressType: CardShipmentAddressType;
      mailingAddress?: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
    } = {
      cardShipmentAddressType: formData[CARD_SHIPMENT_FIELD] as CardShipmentAddressType,
    };

    if (formData[CARD_SHIPMENT_FIELD] === 'MAILING') {
      dto.mailingAddress = {
        line1: formData[OnboardingFormFields.LINE1] || '',
        line2: formData[OnboardingFormFields.APARTMENT] || undefined,
        city: formData[OnboardingFormFields.CITY] || '',
        state: formData[OnboardingFormFields.STATE]?.value || '',
        postalCode: formData[OnboardingFormFields.ZIP] || '',
        country: 'US',
      };
    }

    asyncSetCardShipment(dto);
    syncCardShipmentToStore(formData[CARD_SHIPMENT_FIELD] as CardShipmentAddressType, formData);
  }, [formMethods, asyncSetCardShipment]);

  useSetError({ formMethods });
  useValidateAutoComplete({
    formMethods,
    relevantFields: [
      OnboardingFormFields.LINE1,
      OnboardingFormFields.APARTMENT,
      OnboardingFormFields.CITY,
      OnboardingFormFields.ZIP,
    ],
  });

  return {
    handleSubmit,
    formMethods,
    isPending,
    options,
    showMailingForm,
    selectedType,
    usStates,
  };
};

export { CARD_SHIPMENT_FIELD };
export type { CardShipmentFormValues };
